/**
 * FD native contact dialog — opens/handles the <dialog> rendered by snippets/fd-contact-dialog.liquid.
 * Brief: FD_CC_Brief_Native_Contact_Forms.md (hidden test build, Sep 2026).
 *
 * Same construction as fd-size-guide.js and for the same reason: delegated from `document` with no
 * cached element references, because Horizon morphs product sections on variant change and drops
 * anything bound directly to those nodes. An inline <script> re-inserted by morph never executes,
 * so this lives as an external module — loaded once per page however many times the snippet emits
 * the tag.
 *
 * Trigger contract (set by the snippet / section, read here):
 *   [data-fd-contact-trigger="<dialog id>"]  — opens that dialog.
 *   data-heading="…"                          — optional; replaces the dialog heading text.
 *   data-shop="Camden|Notting Hill|Hackney"   — optional; pre-selects the Preferred shop.
 *                                               PRESENT-BUT-EMPTY resets the select to its
 *                                               placeholder (the generic "Contact us" trigger).
 *                                               ABSENT leaves the select alone (product trigger).
 *   The pre-selection is a plain select.value write — never locked; the customer can change it.
 */

/**
 * Keep contact[Variant] honest on product pages. Horizon patches the buy area on variant change
 * rather than re-rendering it, so the Liquid-rendered value goes stale — and on this store it does
 * NOT maintain a `?variant=` URL param either (verified on the v40 draft, 10 Sep 2026). The
 * authoritative live value is the add-to-cart form's `input[name="id"]` — it has to be right or
 * add-to-cart itself would break. Re-resolved at open AND at submit, against the id→title map the
 * snippet embeds as JSON; the map also scopes the search so a quick-add form for some OTHER
 * product (recommendations etc.) can never match. URL param is kept as a first try for deep links.
 * If neither source matches (e.g. an appointment-mode bike with no cart form), the Liquid default
 * stands.
 */
function resolveVariantTitle(dialog) {
  const map = dialog.querySelector('[data-fd-contact-variants]');
  const input = dialog.querySelector('input[name="contact[Variant]"]');
  if (!map || !input) return;

  let titles;
  try {
    titles = JSON.parse(map.textContent ?? '{}');
  } catch {
    return; // Leave the Liquid-rendered default in place.
  }

  let id = new URLSearchParams(window.location.search).get('variant');
  if (!id || typeof titles[id] !== 'string') {
    id = null;
    for (const el of document.querySelectorAll('form[action*="/cart/add"] input[name="id"]')) {
      if (typeof titles[el.value] === 'string') {
        id = el.value;
        break;
      }
    }
  }

  if (id) input.value = titles[id];
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const trigger = target.closest('[data-fd-contact-trigger]');
  if (trigger instanceof HTMLElement) {
    const dialog = document.getElementById(trigger.dataset.fdContactTrigger ?? '');
    if (dialog instanceof HTMLDialogElement) {
      event.preventDefault();

      const heading = dialog.querySelector('[data-fd-contact-heading]');
      if (heading && trigger.dataset.heading) heading.textContent = trigger.dataset.heading;

      const shopSelect = dialog.querySelector('select[name="contact[Preferred shop]"]');
      if (shopSelect instanceof HTMLSelectElement && 'shop' in trigger.dataset) {
        shopSelect.value = trigger.dataset.shop ?? '';
      }

      resolveVariantTitle(dialog);
      dialog.showModal();
    }
    return;
  }

  if (target.closest('[data-fd-contact-close]')) {
    target.closest('dialog')?.close();
    return;
  }

  // Backdrop: a click on the <dialog> element itself is outside the inner card, because the card
  // covers the whole dialog box. Guarded on our own class so no other dialog is affected.
  if (target instanceof HTMLDialogElement && target.classList.contains('fd-contact-dialog')) {
    target.close();
  }
});

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  const dialog = form.closest('dialog.fd-contact-dialog');
  if (!dialog) return;

  resolveVariantTitle(dialog);

  // Success redirect goes to the shared thank-you page, carrying the chosen shop the same
  // way the old Powerful Forms flow did (?shop=camden etc.). Set at submit time so it
  // always reflects the final selection, whichever trigger opened the dialog.
  const returnTo = form.querySelector('input[name="return_to"]');
  const shopSelect = form.querySelector('select[name="contact[Preferred shop]"]');
  if (returnTo instanceof HTMLInputElement && shopSelect instanceof HTMLSelectElement && shopSelect.value) {
    const slug = shopSelect.value.toLowerCase().replace(/\s+/g, '-');
    returnTo.value = `/pages/enquiry-received?shop=${slug}`;
  }

  // Double-submission guard (15 Sep 2026). A fast double-click can file the same enquiry
  // twice: Shopify's captcha wrapper answers the first click with its own form.submit()
  // and the second click then posts natively — two emails, twin FD Leads rows. Once the
  // captcha reports bound, any submit WILL go out (natively or via the wrapper), so the
  // button is disabled here. While still UNBOUND the wrapper swallows the event and the
  // shopper must be able to click again — so it stays enabled in that case.
  if (form.dataset.hcaptchaBound === 'true' || form.dataset.recaptchaBound === 'true') {
    const btn = form.querySelector('.fd-contact__submit');
    if (btn instanceof HTMLButtonElement) {
      btn.disabled = true;
      btn.classList.add('fd-contact__submit--busy');
    }
  }
});

// Back/forward-cache restores (Safari especially) revive the page with the button still
// disabled from the previous submit — re-arm it so the dialog stays usable.
window.addEventListener('pageshow', () => {
  for (const btn of document.querySelectorAll('.fd-contact__submit:disabled')) {
    btn.disabled = false;
    btn.classList.remove('fd-contact__submit--busy');
  }
});

// After a real submission Shopify round-trips the page and anchors back to the form's element id.
// The dialog is closed on a fresh page load, so the success (or error) state it now contains would
// be invisible — re-open it. Gated on the hash matching OUR form: notify-me on the same product
// page is also a contact form, and form.posted_successfully? is not scoped per form, so a
// notify-me submission must not pop this dialog.
{
  const marker = document.querySelector('[data-fd-contact-autopen]');
  const dialog = marker?.closest('dialog');
  if (
    dialog instanceof HTMLDialogElement &&
    window.location.hash === `#${dialog.dataset.fdContactForm}`
  ) {
    dialog.showModal();
  }
}
